/**
 * Render a branded fallback panel when Angular bootstrap fails (network blip,
 * bad chunk, corrupted env config, etc). Without this the user sees the
 * index.html splash forever, which gives no signal anything went wrong.
 *
 * The styling is intentionally inline so it works even if the styles bundle
 * hasn't loaded. Brand color (#0033aa) matches `$color-background-brand` in
 * the theme tokens.
 */
export function renderBootstrapFailure(err: unknown): void {
  // eslint-disable-next-line no-console
  console.error('[bootstrap] Application failed to start', err);

  const appRoot = document.querySelector('bb-app-root');
  if (appRoot) {
    appRoot.innerHTML = '';
  }

  if (!document.body) {
    return;
  }

  const overlay = document.createElement('div');
  overlay.setAttribute(
    'style',
    [
      'position: fixed',
      'inset: 0',
      'display: flex',
      'align-items: center',
      'justify-content: center',
      'padding: 24px',
      'background: #f5f7fa',
      'font-family: "Open Sans", system-ui, -apple-system, sans-serif',
      'z-index: 2147483647',
    ].join('; '),
  );

  const card = document.createElement('div');
  card.setAttribute(
    'style',
    [
      'max-width: 440px',
      'width: 100%',
      'background: #ffffff',
      'border-radius: 12px',
      'box-shadow: 0 8px 24px rgba(0, 51, 170, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
      'padding: 40px 32px',
      'text-align: center',
    ].join('; '),
  );

  const logo = document.createElement('img');
  logo.setAttribute('src', 'assets/images/sbk-logo.png');
  logo.setAttribute('alt', 'Stanbic Bank');
  logo.setAttribute(
    'style',
    'width: 45%; height: auto; display: block; margin: 0 auto 24px;',
  );
  logo.addEventListener('error', () => {
    logo.style.display = 'none';
  });

  const illustration = document.createElement('img');
  illustration.setAttribute('src', '/assets/images/bb-illustration-ui/ic_scenario_maintenance.svg');
  illustration.setAttribute('alt', 'scenario maintenance');
  illustration.setAttribute('width', '140');
  illustration.setAttribute('height', '140');
  illustration.setAttribute('style', 'display: block; margin: 0 auto 16px;');

  const heading = document.createElement('h1');
  heading.textContent = 'Something went wrong';
  heading.setAttribute(
    'style',
    [
      'margin: 0 0 12px',
      'color: #0a1a3a',
      'font-size: 20px',
      'font-weight: 600',
      'line-height: 1.3',
    ].join('; '),
  );

  const body = document.createElement('p');
  body.textContent =
    "We couldn't load the application. Please refresh the page. If the problem persists, contact customer care on +254711068888 or email: ibkenya@stanbic.com.";
  body.setAttribute(
    'style',
    [
      'margin: 0 0 28px',
      'color: #4a5568',
      'font-size: 14px',
      'line-height: 1.5',
    ].join('; '),
  );

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Refresh';
  button.setAttribute(
    'style',
    [
      'padding: 10px 28px',
      'font-size: 14px',
      'font-weight: 600',
      'font-family: inherit',
      'cursor: pointer',
      'border: none',
      'border-radius: 6px',
      'background: #0033aa',
      'color: #ffffff',
      'transition: background 0.15s ease',
    ].join('; '),
  );
  button.addEventListener('mouseenter', () => {
    button.style.background = '#002280';
  });
  button.addEventListener('mouseleave', () => {
    button.style.background = '#0033aa';
  });
  button.addEventListener('click', () => location.reload());

  card.appendChild(logo);
  card.appendChild(illustration);
  card.appendChild(heading);
  card.appendChild(body);
  card.appendChild(button);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}
